import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icon broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ROUTING_BASE = 'https://test-map.slq.kz/route/v1/driving';
const NEAREST_BASE = 'https://test-map.slq.kz/nearest/v1/driving';
const AIRPLANE_ICON_SRC = `${process.env.PUBLIC_URL || ''}/airplane-icon.svg`;
const KAZAKHSTAN_CENTER = [48.0196, 66.9237];
const SEGMENT_COLORS = ['#2563EB', '#F97316', '#16A34A', '#9333EA', '#DC2626', '#0891B2', '#CA8A04'];
const MODE_AIR = 'air';
const MODE_CAR = 'car';

const normalizeRouteToken = (value) => String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');

const isSamePoint = (a, b) => {
    if (!a || !b || a.length !== 2 || b.length !== 2) {
        return false;
    }
    return Math.abs(a[0] - b[0]) < 1e-7 && Math.abs(a[1] - b[1]) < 1e-7;
};

const haversineDistance = (from, to) => {
    const [lat1, lon1] = from;
    const [lat2, lon2] = to;
    const toRad = (deg) => deg * Math.PI / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const buildAnimationTrackFromSegments = (segments) => {
    const points = [];
    const cumulative = [0];
    const ranges = [];
    let total = 0;

    const addPoint = (point) => {
        if (!point || point.length !== 2) {
            return;
        }
        const [lat, lon] = point;
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return;
        }
        const nextPoint = [lat, lon];
        const lastPoint = points[points.length - 1];
        if (!lastPoint) {
            points.push(nextPoint);
            return;
        }
        if (isSamePoint(lastPoint, nextPoint)) {
            return;
        }
        total += haversineDistance(lastPoint, nextPoint);
        points.push(nextPoint);
        cumulative.push(total);
    };

    (segments || []).forEach((segment) => {
        const segPoints = (segment?.positions || [])
            .filter((point) => Array.isArray(point) && point.length === 2);
        if (segPoints.length < 2) {
            return;
        }

        const segStart = total;
        addPoint(segPoints[0]);
        for (let i = 1; i < segPoints.length; i++) {
            addPoint(segPoints[i]);
        }
        const segEnd = total;

        if (segEnd > segStart) {
            ranges.push({
                start: segStart,
                end: segEnd,
                mode: segment?.mode || MODE_CAR,
            });
        }
    });

    if (!points.length) {
        return { points: [], cumulative: [0], ranges: [], total: 0 };
    }

    return { points, cumulative, ranges, total };
};

const getPointOnTrack = (track, distance) => {
    if (!track.points.length) {
        return null;
    }
    if (track.points.length === 1 || track.total <= 0) {
        return track.points[0];
    }

    const wrappedDistance = ((distance % track.total) + track.total) % track.total;
    for (let i = 1; i < track.cumulative.length; i++) {
        const segStartDist = track.cumulative[i - 1];
        const segEndDist = track.cumulative[i];
        if (wrappedDistance <= segEndDist) {
            const ratio = (wrappedDistance - segStartDist) / Math.max(segEndDist - segStartDist, 1);
            const [lat1, lon1] = track.points[i - 1];
            const [lat2, lon2] = track.points[i];
            return [
                lat1 + (lat2 - lat1) * ratio,
                lon1 + (lon2 - lon1) * ratio,
            ];
        }
    }

    return track.points[track.points.length - 1];
};

const getModeOnTrack = (track, distance) => {
    if (!track?.ranges?.length || !track.total) {
        return MODE_CAR;
    }
    const wrappedDistance = ((distance % track.total) + track.total) % track.total;
    const matched = track.ranges.find((range) => wrappedDistance >= range.start && wrappedDistance <= range.end);
    return matched?.mode || track.ranges[track.ranges.length - 1]?.mode || MODE_CAR;
};

const fetchRoute = async (fromLat, fromLon, toLat, toLon) => {
    try {
        const res = await fetch(
            `${ROUTING_BASE}/${fromLon},${fromLat};${toLon},${toLat}?overview=full&geometries=geojson`
        );
        const data = await res.json();
        if (data.routes?.length > 0) {
            // OSRM returns [lon, lat] — swap to [lat, lon] for Leaflet
            return data.routes[0].geometry.coordinates.map(([lon, lat]) => [lat, lon]);
        }
    } catch (e) {
        console.error('Routing error:', e);
    }
    return null;
};

const fetchNearestPoint = async (lat, lon) => {
    try {
        const res = await fetch(`${NEAREST_BASE}/${lon},${lat}?number=1`);
        const data = await res.json();
        const nearest = data?.waypoints?.[0]?.location;
        if (Array.isArray(nearest) && nearest.length === 2) {
            const [nearestLon, nearestLat] = nearest;
            return [nearestLat, nearestLon];
        }
    } catch (e) {
        console.error('Nearest error:', e);
    }
    return null;
};

const FitBounds = ({ positions, fitKey }) => {
    const map = useMap();
    const lastAppliedKeyRef = useRef('');

    useEffect(() => {
        if (!fitKey || fitKey === lastAppliedKeyRef.current) {
            return;
        }

        if (positions?.length >= 2) {
            map.fitBounds(positions, { padding: [50, 50] });
        } else if (positions?.length === 1) {
            map.setView(positions[0], Math.max(map.getZoom(), 12), { animate: true });
        }
        lastAppliedKeyRef.current = fitKey;
    }, [positions, fitKey, map]);
    return null;
};

const InvalidateMapSize = () => {
    const map = useMap();

    useEffect(() => {
        const invalidate = () => map.invalidateSize({ animate: false });
        const frameId = requestAnimationFrame(invalidate);
        const timerId = setTimeout(invalidate, 180);

        const container = map.getContainer();
        let observer;

        if (typeof ResizeObserver !== 'undefined' && container) {
            observer = new ResizeObserver(() => invalidate());
            observer.observe(container);
        }

        const handleWindowResize = () => invalidate();
        window.addEventListener('resize', handleWindowResize);

        return () => {
            cancelAnimationFrame(frameId);
            clearTimeout(timerId);
            if (observer) observer.disconnect();
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [map]);

    return null;
};

const PinZoomControl = () => {
    const map = useMap();

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const mapContainer = map.getContainer();
        let zoomContainer = null;
        let rafId = null;
        let listenersAttached = false;

        const updatePosition = () => {
            if (!zoomContainer) {
                return;
            }

            const rect = mapContainer.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const margin = viewportWidth <= 900 ? 10 : 14;

            const isVisible = rect.bottom > 0
                && rect.top < viewportHeight
                && rect.right > 0
                && rect.left < viewportWidth;

            if (!isVisible) {
                zoomContainer.style.opacity = '0';
                zoomContainer.style.pointerEvents = 'none';
                return;
            }

            const rightOffset = Math.max(margin, viewportWidth - rect.right + margin);
            const bottomOffset = Math.max(margin, viewportHeight - rect.bottom + margin);

            zoomContainer.style.position = 'fixed';
            zoomContainer.style.right = `${rightOffset}px`;
            zoomContainer.style.bottom = `${bottomOffset}px`;
            zoomContainer.style.zIndex = '1200';
            zoomContainer.style.opacity = '1';
            zoomContainer.style.pointerEvents = 'none';

            const zoomControls = zoomContainer.querySelectorAll('.leaflet-control');
            zoomControls.forEach((control) => {
                control.style.margin = '0';
                control.style.pointerEvents = 'auto';
            });
        };

        const attachListeners = () => {
            if (listenersAttached) {
                return;
            }

            listenersAttached = true;
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, { passive: true });
            map.on('move', updatePosition);
            map.on('zoom', updatePosition);
            map.on('resize', updatePosition);
        };

        const init = () => {
            zoomContainer = mapContainer?.querySelector('.leaflet-control-container .leaflet-bottom.leaflet-right');

            if (!zoomContainer) {
                rafId = requestAnimationFrame(init);
                return;
            }

            updatePosition();
            attachListeners();
        };

        init();

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            if (listenersAttached) {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition);
                map.off('move', updatePosition);
                map.off('zoom', updatePosition);
                map.off('resize', updatePosition);
            }

            if (zoomContainer) {
                zoomContainer.style.position = '';
                zoomContainer.style.right = '';
                zoomContainer.style.bottom = '';
                zoomContainer.style.zIndex = '';
                zoomContainer.style.opacity = '';
                zoomContainer.style.pointerEvents = '';

                const zoomControls = zoomContainer.querySelectorAll('.leaflet-control');
                zoomControls.forEach((control) => {
                    control.style.margin = '';
                    control.style.pointerEvents = '';
                });
            }
        };
    }, [map]);

    return null;
};

const defaultMapStyle = { height: '600px', width: '100%', borderRadius: '8px' };

const RouteMap = ({
    paths,
    className,
    mapStyle,
    searchMarkers = [],
    vectors = [],
    airExpressCodes = [],
    segmentColors = SEGMENT_COLORS,
    hoveredSegmentIndex = null,
    onSegmentHoverChange,
}) => {
    const [routeSegments, setRouteSegments] = useState([]);
    const [animationDistance, setAnimationDistance] = useState(0);
    const animationFrameRef = useRef(null);
    const segmentLayerRefs = useRef([]);

    const validPaths = useMemo(
        () => (paths?.filter((p) => p.lat && p.lon) || []),
        [paths]
    );
    const airExpressPsNameSet = useMemo(
        () => new Set((airExpressCodes || []).map((code) => normalizeRouteToken(code))),
        [airExpressCodes]
    );

    useEffect(() => {
        if (validPaths.length < 2) {
            setRouteSegments([]);
            return;
        }

        const fetchAllSegments = async () => {
            const segments = [];
            for (let i = 0; i < validPaths.length - 1; i++) {
                const p1 = validPaths[i];
                const p2 = validPaths[i + 1];
                const vector = vectors?.[i] || {};
                const segmentPsName = vector?.psName || '';
                const isExpressSegment = airExpressPsNameSet.has(normalizeRouteToken(segmentPsName));

                if (isExpressSegment) {
                    const nearestFrom = await fetchNearestPoint(p1.lat, p1.lon);
                    const nearestTo = await fetchNearestPoint(p2.lat, p2.lon);
                    const from = nearestFrom || [p1.lat, p1.lon];
                    const toCandidate = nearestTo || [p2.lat, p2.lon];
                    const to = isSamePoint(from, toCandidate) ? [p2.lat, p2.lon] : toCandidate;
                    segments.push({
                        positions: [from, to],
                        color: segmentColors[i % segmentColors.length],
                        dashed: true,
                        mode: MODE_AIR,
                    });
                    continue;
                }

                const coords = await fetchRoute(p1.lat, p1.lon, p2.lat, p2.lon);
                segments.push({
                    positions: coords || [[p1.lat, p1.lon], [p2.lat, p2.lon]],
                    color: segmentColors[i % segmentColors.length],
                    dashed: false,
                    mode: MODE_CAR,
                });
            }
            setRouteSegments(segments);
        };

        fetchAllSegments();
    }, [paths, vectors, airExpressPsNameSet, segmentColors]);

    const animationTrack = useMemo(
        () => buildAnimationTrackFromSegments(routeSegments),
        [routeSegments]
    );

    useEffect(() => {
        setAnimationDistance(0);
    }, [animationTrack.total]);

    useEffect(() => {
        if (animationTrack.total <= 0) {
            return undefined;
        }

        let lastTime = null;
        const hasAirSegments = routeSegments.some((segment) => segment.mode === MODE_AIR);
        const targetLoopSeconds = hasAirSegments ? 95 : 120;
        const speedMetersPerSecond = Math.max(3, animationTrack.total / targetLoopSeconds);

        const tick = (timestamp) => {
            if (lastTime == null) {
                lastTime = timestamp;
            }
            const deltaSeconds = (timestamp - lastTime) / 1000;
            lastTime = timestamp;

            setAnimationDistance((prev) => {
                const next = prev + speedMetersPerSecond * deltaSeconds;
                return next >= animationTrack.total ? next - animationTrack.total : next;
            });

            animationFrameRef.current = requestAnimationFrame(tick);
        };

        animationFrameRef.current = requestAnimationFrame(tick);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [animationTrack.total, routeSegments]);

    useEffect(() => {
        if (hoveredSegmentIndex == null) {
            return;
        }
        const hoveredLayer = segmentLayerRefs.current[hoveredSegmentIndex];
        if (hoveredLayer && typeof hoveredLayer.bringToFront === 'function') {
            hoveredLayer.bringToFront();
        }
    }, [hoveredSegmentIndex, routeSegments.length]);

    const animatedPoint = useMemo(
        () => getPointOnTrack(animationTrack, animationDistance),
        [animationTrack, animationDistance]
    );
    const animatedMode = useMemo(
        () => getModeOnTrack(animationTrack, animationDistance),
        [animationTrack, animationDistance]
    );

    const vehicleIcon = useMemo(
        () => L.divIcon({
            className: '',
            html: animatedMode === MODE_AIR
                ? `<div style="width:30px;height:30px;border-radius:50%;background:#fff;box-shadow:0 6px 14px rgba(20,53,89,.25);display:flex;align-items:center;justify-content:center;border:1px solid #c7d9ee;">
                     <img src="${AIRPLANE_ICON_SRC}" alt="" style="width:17px;height:17px;object-fit:contain;transform:rotate(24deg);" />
                   </div>`
                : `<div style="width:30px;height:30px;border-radius:50%;background:#1f6fc0;box-shadow:0 6px 14px rgba(20,53,89,.25);display:flex;align-items:center;justify-content:center;border:1px solid #a6c6e6;">
                     <i class="pi pi-truck" style="font-size:14px;color:#fff;"></i>
                   </div>`,
            iconSize: [30, 30],
            iconAnchor: [15, 15],
        }),
        [animatedMode]
    );

    const safeSearchMarkers = useMemo(
        () => (searchMarkers || []).filter((m) => Number.isFinite(Number(m.lat)) && Number.isFinite(Number(m.lon))),
        [searchMarkers]
    );
    const bounds = useMemo(() => {
        const markerBounds = safeSearchMarkers.map((m) => [Number(m.lat), Number(m.lon)]);
        const routeBounds = validPaths.map((p) => [p.lat, p.lon]);
        return [...routeBounds, ...markerBounds];
    }, [safeSearchMarkers, validPaths]);
    const boundsKey = useMemo(
        () => bounds.map(([lat, lon]) => `${lat.toFixed(6)},${lon.toFixed(6)}`).join(';'),
        [bounds]
    );

    return (
        <MapContainer
            center={KAZAKHSTAN_CENTER}
            zoom={6}
            className={className}
            style={mapStyle || defaultMapStyle}
            scrollWheelZoom={false}
            zoomControl={false}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://test-map.slq.kz/{z}/{x}/{y}.png"
            />
            {validPaths.map((p, i) => (
                <Marker key={i} position={[p.lat, p.lon]}>
                    <Popup>{p.name}</Popup>
                </Marker>
            ))}
            {safeSearchMarkers.map((marker, i) => (
                <CircleMarker
                    key={`search_marker_${marker.type || 'point'}_${i}`}
                    center={[Number(marker.lat), Number(marker.lon)]}
                    radius={10}
                    color={marker.type === 'receiver' ? '#D97706' : '#2563EB'}
                    fillColor={marker.type === 'receiver' ? '#F59E0B' : '#3B82F6'}
                    fillOpacity={0.85}
                    weight={3}
                >
                    <Popup>
                        <strong>{marker.type === 'receiver' ? 'Получатель' : 'Отправитель'}</strong>
                        <br />
                        {marker.label || `${marker.lat}, ${marker.lon}`}
                    </Popup>
                </CircleMarker>
            ))}
            {routeSegments.map((segment, i) => {
                const hasHoveredSegment = hoveredSegmentIndex != null;
                const isHovered = hoveredSegmentIndex === i;
                const isMuted = hasHoveredSegment && !isHovered;
                return [
                    <Polyline
                        key={`segment_${i}`}
                        ref={(layer) => {
                            segmentLayerRefs.current[i] = layer || null;
                        }}
                        positions={segment.positions}
                        color={segment.color}
                        className={`route-segment-path ${isHovered ? 'route-segment-path-active' : ''} ${isMuted ? 'route-segment-path-muted' : ''}`}
                        weight={segment.mode === MODE_AIR ? (isHovered ? 7 : 5) : (isHovered ? 6 : 4)}
                        opacity={isHovered ? 1 : (isMuted ? 0.34 : 0.86)}
                        dashArray={segment.mode === MODE_AIR ? '16 12' : undefined}
                        lineCap={segment.mode === MODE_AIR ? 'butt' : 'round'}
                        eventHandlers={{
                            mouseover: (event) => {
                                event.target.bringToFront();
                                if (onSegmentHoverChange) {
                                    onSegmentHoverChange(i);
                                }
                            },
                            mouseout: () => {
                                if (onSegmentHoverChange) {
                                    onSegmentHoverChange(null);
                                }
                            },
                        }}
                    />,
                    isHovered && (
                        <Polyline
                            key={`segment_pulse_${i}`}
                            positions={segment.positions}
                            color={segment.color}
                            className="route-segment-path-pulse"
                            weight={segment.mode === MODE_AIR ? 11 : 10}
                            opacity={0.45}
                            dashArray={segment.mode === MODE_AIR ? '16 12' : undefined}
                            lineCap={segment.mode === MODE_AIR ? 'butt' : 'round'}
                            interactive={false}
                        />
                    ),
                ];
            })}
            {animatedPoint && (
                <Marker position={animatedPoint} icon={vehicleIcon}>
                    <Popup>{animatedMode === MODE_AIR ? 'Авиа маршрут' : 'Авто маршрут'}</Popup>
                </Marker>
            )}
            <InvalidateMapSize />
            {!!bounds.length && <FitBounds positions={bounds} fitKey={boundsKey} />}
            <ZoomControl position="bottomright" />
            <PinZoomControl />
        </MapContainer>
    );
};

export default RouteMap;
